<template>
  <div
    v-if="showRelatedSearchResults || projects.length"
    class="m-related-materials"
  >
    <!-- related people & groups | collections -->
    <template v-for="(relatedMaterialsKey, index) in sectionsToRender">
      <RelatedSearchResults
        v-if="storeObject?.[relatedMaterialsKey]?.result"
        :key="`${relatedMaterialsKey}-${index}`"
        v-bind="getResultsProps(relatedMaterialsKey, storeObject.type)"
      />
    </template>
    <!-- related resources section -->
    <div
      v-if="projects.length"
      ref="projects"
      class="m-related-materials__projects"
    >
      <div class="m-related-materials__related-resources-headings">
        <h2 class="m-related-materials__heading">
          {{ translate("m-related-materials.resources_heading") }}
        </h2>
        <h3 class="m-related-materials__subheading">
          {{ translate("m-related-materials.resources_subheading") }}
        </h3>
      </div>
      <div class="m-related-materials__related-resources-items">
        <div
          v-for="item in projects"
          :key="item.title"
          class="m-related-materials__project"
        >
          <GuiLink
            class="m-related-materials__project-heading-link"
            :href="item.link"
            icon-name="outbound"
            with-icon="after"
          >
            {{ item.title }}
          </GuiLink>
          <p class="m-related-materials__project-description">
            {{ item.description }}
          </p>
          <p class="m-related-materials__project-contributor">
            {{ translate("human_made_object.related_projects_contributor") }}:
            {{ item.contributor }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RelatedSearchResults from "@/components/molecules/relatedSearchResults/index.vue";
import { GuiLink } from "@thegetty-private/getty-ui";
import * as configHelper from "@/helpers/configHelper";
import * as collectionHelpers from "@/helpers/CollectionInformationHelper";
import { localizationMixin } from "@thegetty-private/getty-ui";

export default {
  name: "RelatedMaterials",
  components: {
    GuiLink,
    RelatedSearchResults
  },
  mixins: [localizationMixin],
  props: {
    /**
     * The base locale key for the record metadata field component
     */
    baseLocaleKey: {
      type: String,
      default: "m-related-materials"
    },
    /**
     * A collection ID
     */
    collectionId: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * Object from store with related materials data [actor, collection, component, object]
     */
    storeObject: {
      type: Object,
      default: null
    },
    /**
     * Related materials sections to render based on valid related materials
     * keys for an entity in the store
     */
    sectionsToRender: {
      type: Array,
      default: () => [],
      validator: (value) =>
        value.every((e) =>
          [
            "depicts",
            "relatedActors",
            "relatedCollections",
            "relatedItems"
          ].includes(e)
        )
    }
  },
  computed: {
    /**
     * Returns an array of projects to display based on the site config and collection
     * helpers
     *
     * @returns {array} - an empty array or an array with a project data object
     */
    projects() {
      if (
        configHelper.is12SunsetsDisabled() == false &&
        collectionHelpers.is12Sunsets(this.collectionId)
      ) {
        return [
          {
            title: "12 Sunsets",
            contributor: "Stamen Design",
            description:
              "12 Sunsets is an interactive experience allowing users to explore and experience Edward Ruscha's Streets of Los Angeles",
            link: "http://12sunsets.getty.edu"
          }
        ];
      }
      return [];
    },
    /**
     * Whether to display the related search results
     *
     * @returns {boolean}
     */
    showRelatedSearchResults() {
      return this.sectionsToRender.find(
        (e) => this.storeObject?.[e]?.total > 0
      );
    }
  },
  methods: {
    /**
     * Related search results component props based on related materials key
     *
     * @param {string} - valid related materials key
     * @param {string} - type
     *
     * @returns {object} - related search results props
     */
    getResultsProps(key, type) {
      return {
        heading: this.getHeading(key),
        subheading: this.getSubheading(key, type),
        cypressSuffix: key,
        searchParams: this.storeObject?.[key]?.params,
        searchResults: this.storeObject?.[key]?.result,
        total: this.storeObject?.[key]?.total,
        showCount: true
      };
    },
    /**
     * Return related search results component heading
     *
     * @param {string} - related materials key
     *
     * @returns {string}
     */
    getHeading(key) {
      const localeKey = `${key.toLowerCase()}_heading`;
      return this.$te(`${this.baseLocaleKey}.${localeKey}`)
        ? this.translatePart(localeKey)
        : undefined;
    },
    /**
     * Return related search results component subheading
     *
     * @param {string} - related materials key
     * @param {string} - type
     *
     * @returns {string}
     */
    getSubheading(key, type = "") {
      let type_ = type.toLowerCase();
      let key_ = key.toLowerCase();
      if (["person", "group"].includes(type_)) {
        key_ = `${key_}_${type_}`;
      }
      const localeKey = `${key_}_subheading`;

      return this.$te(`${this.baseLocaleKey}.${localeKey}`)
        ? this.translatePart(localeKey)
        : undefined;
    }
  }
};
</script>
<style lang="scss">
@import "./index";
</style>
